import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  PASSWORD_MIN_LENGTH_FLOOR,
  QAQC_METRIC_CATEGORY_LABELS,
  isAppKey,
  pqiWeightsFrom,
  type AppKey,
} from '@qms360/shared';
import { AppTopBar, Breadcrumbs } from '../../components/AppTopBar.js';
import { NotificationTemplatesSection } from '../../components/admin/NotificationTemplates.js';
import { PageMessage } from '../AppHome.js';
import { ApiError } from '../../lib/api.js';
import {
  addHoliday,
  createBenchmark,
  createBusinessUnit,
  createDiscipline,
  createWeightSet,
  fetchMasterData,
  retire,
  updateCalendar,
  updateEscalationRule,
  updateOrganisation,
  type MasterDataSnapshot,
} from '../../lib/master-data-api.js';

import { ConfirmButton } from '../../components/ConfirmButton.js';
import { t } from '../../lib/i18n.js';
/**
 * The Master Data & Configuration Center (spec §13.5).
 *
 * This screen is what makes Rule 1 true. Every value QMS360 refuses to hardcode
 * already lived in the database, but with no way for a business user to change
 * any of it, *"an Org Admin changes this with no deploy"* was a promise the
 * product could not keep — and a correction still meant a developer with `psql`,
 * which Rule 9 forbids outright.
 *
 * Two things the screen insists on, both because configuration is quiet:
 *
 *  - **Where a value bites is stated next to it.** A closure-rate target is not
 *    a number in a form; it is the thing three escalation rules compare against.
 *    Somebody changing it should not have to already know that.
 *  - **Projects are shown as synced, not as disabled.** Spec §13.5 is explicit —
 *    a greyed-out "New project" button would teach a workflow that does not
 *    exist in production.
 */

const WEEKDAYS = [
  { iso: 1, label: 'Mon' },
  { iso: 2, label: 'Tue' },
  { iso: 3, label: 'Wed' },
  { iso: 4, label: 'Thu' },
  { iso: 5, label: 'Fri' },
  { iso: 6, label: 'Sat' },
  { iso: 7, label: 'Sun' },
];

export function MasterData() {
  const params = useParams<{ app: string }>();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const config = useQuery({ queryKey: ['master-data'], queryFn: fetchMasterData });

  const refresh = () => {
    setError(null);
    void queryClient.invalidateQueries({ queryKey: ['master-data'] });
  };
  const fail = (fallback: string) => (caught: unknown) =>
    setError(caught instanceof ApiError ? caught.message : fallback);

  const calendar = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      updateCalendar(id, body),
    onSuccess: refresh,
    onError: fail(t("The working calendar could not be saved.")),
  });

  const organisation = useMutation({
    mutationFn: (body: Record<string, unknown>) => updateOrganisation(body),
    onSuccess: refresh,
    onError: fail(t("The sign-in settings could not be saved.")),
  });

  const holiday = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { holidayDate: string; name: string } }) =>
      addHoliday(id, body),
    onSuccess: refresh,
    onError: fail(t("That holiday could not be added.")),
  });

  const weights = useMutation({
    mutationFn: (body: Record<string, unknown>) => createWeightSet(body),
    onSuccess: refresh,
    onError: fail(t("That weighting could not be saved.")),
  });

  const rule = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      updateEscalationRule(id, body),
    onSuccess: refresh,
    onError: fail(t("That escalation rule could not be saved.")),
  });

  const benchmark = useMutation({
    mutationFn: (body: Record<string, unknown>) => createBenchmark(body),
    onSuccess: refresh,
    onError: fail(t("That target could not be saved.")),
  });

  const businessUnit = useMutation({
    mutationFn: (body: Record<string, unknown>) => createBusinessUnit(body),
    onSuccess: refresh,
    onError: fail(t("That business unit could not be saved.")),
  });

  const discipline = useMutation({
    mutationFn: (body: Record<string, unknown>) => createDiscipline(body),
    onSuccess: refresh,
    onError: fail(t("That discipline could not be saved.")),
  });

  const retirement = useMutation({
    mutationFn: ({ kind, id }: { kind: string; id: string }) => retire(kind, id),
    onSuccess: refresh,
    onError: fail(t("That could not be retired.")),
  });

  const app = params.app;
  if (!app || !isAppKey(app)) {
    return <PageMessage title={t("Unknown application")} body="That application does not exist." />;
  }

  const data = config.data;

  return (
    <div className="min-h-full">
      <AppTopBar app={app as AppKey} />
      <Breadcrumbs app={app as AppKey} trail={['Settings', 'Master data']} />

      <main className="mx-auto max-w-5xl px-4 pb-16 pt-4">
        <h1 className="m-0 text-2xl font-semibold">{t("Master data & configuration")}</h1>
        <p className="mt-1 text-sm text-content-secondary">
          {t("Shared across all three applications. Nothing here needs a deployment to change — that is the point of it.")}
        </p>

        {error && (
          <p role="alert" className="mt-4 text-sm" style={{ color: 'var(--tone-rejected)' }}>
            {error}
          </p>
        )}

        {config.isLoading && <p className="mt-6 text-content-secondary">{t("Loading…")}</p>}

        {config.error && (
          <section className="mt-6 rounded-md border border-border bg-tone-info-surface p-6" role="status">
            <h2 className="m-0 text-base font-semibold">{t("Not available to your role")}</h2>
            <p className="mb-0 mt-2 text-sm text-content-secondary">
              {t("Configuration decides how the whole organisation is measured and chased, so it needs configuration authority rather than ordinary access.")}
            </p>
          </section>
        )}

        {data && (
          <>
            <CalendarSection
              data={data}
              busy={calendar.isPending || holiday.isPending}
              onSaveWeek={(id, workingWeekdays) => calendar.mutate({ id, body: { workingWeekdays } })}
              onAddHoliday={(id, body) => holiday.mutate({ id, body })}
            />

            <WeightsSection data={data} busy={weights.isPending} onCreate={weights.mutate} />

            <SignInSection
              minLength={data.organisation.passwordMinLength}
              busy={organisation.isPending}
              onSave={(passwordMinLength) => organisation.mutate({ passwordMinLength })}
            />

            <RetentionSection
              months={data.organisation.auditLogOnlineMonths}
              busy={organisation.isPending}
              onSave={(auditLogOnlineMonths) => organisation.mutate({ auditLogOnlineMonths })}
            />

            <ReportSettingsSection
              windowMonths={data.organisation.ncRecurrenceWindowMonths}
              buckets={data.organisation.auditAgeingBuckets}
              busy={organisation.isPending}
              onSave={(body) => organisation.mutate(body)}
            />

            <EscalationSection
              data={data}
              busy={rule.isPending}
              onToggle={(id, isEnabled) => rule.mutate({ id, body: { isEnabled } })}
              onPatch={(id, body) => rule.mutate({ id, body })}
            />

            <NotificationTemplatesSection />

            <BenchmarkSection data={data} busy={benchmark.isPending} onCreate={benchmark.mutate} />

            <CodeListSection
              heading={t("Business units")}
              note={t("Departments. A corrective action is owned by one of these, so retiring one leaves the work it owns intact and simply stops it being chosen again.")}
              rows={data.businessUnits.map((unit) => ({ id: unit.id, label: `${unit.code} — ${unit.name}` }))}
              busy={businessUnit.isPending || retirement.isPending}
              onCreate={(code, name) => businessUnit.mutate({ code, name })}
              onRetire={(id) => retirement.mutate({ kind: 'business-units', id })}
            />

            <CodeListSection
              heading={t("Disciplines")}
              note={t("Feeds the dropdowns in App 1 and App 2 — nobody types a discipline by hand.")}
              rows={data.disciplines.map((row) => ({ id: row.id, label: `${row.code} — ${row.name}` }))}
              busy={discipline.isPending || retirement.isPending}
              onCreate={(code, name) => discipline.mutate({ code, name })}
              onRetire={(id) => retirement.mutate({ kind: 'disciplines', id })}
            />

            <CategorisationSection data={data} />
            <ProjectsSection data={data} />
          </>
        )}
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------

function Section({ heading, note, children }: { heading: string; note: string; children: React.ReactNode }) {
  return (
    <section className="mt-8 rounded-md border border-border bg-surface p-5">
      <h2 className="m-0 text-lg font-semibold">{heading}</h2>
      <p className="mt-1 text-sm text-content-secondary">{note}</p>
      <div className="mt-4">{children}</div>
    </section>
  );
}

/**
 * The password rule for local sign-in (Rule 1: a business setting, changed here
 * with no deploy). Applies to dev and UAT sign-in only — in production each app
 * receives its identity from the Algihaz platform and there is no password here.
 */
function SignInSection({
  minLength,
  busy,
  onSave,
}: {
  minLength: number;
  busy: boolean;
  onSave: (minLength: number) => void;
}) {
  const [value, setValue] = useState(String(minLength));
  const parsed = Number(value);
  const valid = Number.isInteger(parsed) && parsed >= PASSWORD_MIN_LENGTH_FLOOR && parsed <= 64;

  return (
    <Section
      heading={t("Sign-in")}
      note={t("For accounts that sign in with a QMS360 password (development and UAT). Takes effect the next time anyone chooses a password; existing passwords are not affected.")}
    >
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (valid) onSave(parsed);
        }}
      >
        <label className="flex flex-col text-sm">
          <span className="mb-1 text-content-secondary">{t("Minimum password length")}</span>
          <input
            type="number"
            min={PASSWORD_MIN_LENGTH_FLOOR}
            max={64}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            className="w-28 rounded border border-border px-2 py-1"
          />
        </label>
        <button
          type="submit"
          disabled={!valid || busy || parsed === minLength}
          className="rounded bg-ag-purple px-3 py-1.5 text-sm text-white disabled:opacity-60"
        >
          {t("Save")}
        </button>
        <span className="text-xs text-content-secondary">
          {t("Between")}{' '}{PASSWORD_MIN_LENGTH_FLOOR}{' '}{t("and 64. The floor of")}{' '}{PASSWORD_MIN_LENGTH_FLOOR}{' '}{t("is fixed.")}
        </span>
      </form>
    </Section>
  );
}

/**
 * The audit log's retention policy (spec §13.7). It states a period and moves
 * nothing: entries are never deleted (Rule 2), and archiving old ones is done by
 * operations, outside the application. Empty means no period is set.
 */
function RetentionSection({
  months,
  busy,
  onSave,
}: {
  months: number | null;
  busy: boolean;
  onSave: (months: number | null) => void;
}) {
  const [value, setValue] = useState(months === null ? '' : String(months));
  const parsed = value.trim() === '' ? null : Number(value);
  const valid = parsed === null || (Number.isInteger(parsed) && parsed >= 12 && parsed <= 600);

  return (
    <Section
      heading={t("Audit log retention")}
      note={t("How long entries stay in the live log before they may be archived. Entries are never deleted, by anyone — archiving moves old entries elsewhere and is done by operations, not by QMS360.")}
    >
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (valid) onSave(parsed);
        }}
      >
        <label className="flex flex-col text-sm">
          <span className="mb-1 text-content-secondary">{t("Keep online for (months)")}</span>
          <input
            type="number"
            min={12}
            max={600}
            placeholder={t("No period set")}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            className="w-32 rounded border border-border px-2 py-1"
          />
        </label>
        <button
          type="submit"
          disabled={!valid || busy || parsed === months}
          className="rounded bg-ag-purple px-3 py-1.5 text-sm text-white disabled:opacity-60"
        >
          {t("Save")}
        </button>
        <span className="text-xs text-content-secondary">
          {t("At least 12. Leave empty to keep every entry online.")}
        </span>
      </form>
    </Section>
  );
}

/**
 * Report settings (spec §13.8, Rule 1): what counts as a recurring
 * nonconformity, and the ageing report's bands. Both are assumptions for
 * Algihaz to confirm (OPEN_QUESTIONS Q23), so they are data, not code.
 */
function ReportSettingsSection({
  windowMonths,
  buckets,
  busy,
  onSave,
}: {
  windowMonths: number;
  buckets: number[];
  busy: boolean;
  onSave: (body: { ncRecurrenceWindowMonths: number; auditAgeingBuckets: number[] }) => void;
}) {
  const [months, setMonths] = useState(String(windowMonths));
  const [bands, setBands] = useState(buckets.join(', '));
  const parsedMonths = Number(months);
  const parsedBands = bands
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map(Number);
  const monthsValid = Number.isInteger(parsedMonths) && parsedMonths >= 1 && parsedMonths <= 60;
  const bandsValid =
    parsedBands.length >= 1 &&
    parsedBands.length <= 6 &&
    parsedBands.every((value, index) => Number.isInteger(value) && value >= 1 && (index === 0 || value > parsedBands[index - 1]!));

  return (
    <Section
      heading={t("Reports")}
      note={t("What the audit reports count. A nonconformity against the same clause in the same department within the window is a recurrence; open corrective actions are grouped by age into the bands.")}
    >
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (monthsValid && bandsValid) onSave({ ncRecurrenceWindowMonths: parsedMonths, auditAgeingBuckets: parsedBands });
        }}
      >
        <label className="flex flex-col text-sm">
          <span className="mb-1 text-content-secondary">{t("Recurrence window (months)")}</span>
          <input
            type="number"
            min={1}
            max={60}
            value={months}
            onChange={(event) => setMonths(event.target.value)}
            className="w-28 rounded border border-border px-2 py-1"
          />
        </label>
        <label className="flex flex-col text-sm">
          <span className="mb-1 text-content-secondary">{t("Ageing bands (days, ascending)")}</span>
          <input
            type="text"
            value={bands}
            placeholder="30, 60, 90"
            onChange={(event) => setBands(event.target.value)}
            className="w-40 rounded border border-border px-2 py-1"
          />
        </label>
        <button
          type="submit"
          disabled={!monthsValid || !bandsValid || busy}
          className="rounded bg-ag-purple px-3 py-1.5 text-sm text-white disabled:opacity-60"
        >
          {t("Save")}
        </button>
        {!bandsValid && <span className="text-xs" style={{ color: 'var(--tone-critical)' }}>{t("Each band must be longer than the one before.")}</span>}
      </form>
    </Section>
  );
}

function CalendarSection({
  data,
  busy,
  onSaveWeek,
  onAddHoliday,
}: {
  data: MasterDataSnapshot;
  busy: boolean;
  onSaveWeek: (id: string, weekdays: number[]) => void;
  onAddHoliday: (id: string, body: { holidayDate: string; name: string }) => void;
}) {
  const calendar = data.calendar;
  const [week, setWeek] = useState<number[]>(calendar?.workingWeekdays ?? []);
  const [date, setDate] = useState('');
  const [name, setName] = useState('');

  if (!calendar) return null;

  const toggle = (iso: number) =>
    setWeek((current) =>
      current.includes(iso) ? current.filter((day) => day !== iso) : [...current, iso].sort(),
    );

  return (
    <Section
      heading={t("Working calendar")}
      note={t("Every SLA clock in QMS360 counts working days against this. Changing it changes when everything is due.")}
    >
      <div className="flex flex-wrap gap-2">
        {WEEKDAYS.map((day) => (
          <button
            key={day.iso}
            type="button"
            aria-pressed={week.includes(day.iso)}
            className={`rounded px-3 py-1.5 text-sm ${
              week.includes(day.iso) ? 'bg-ag-purple text-white' : 'border border-border'
            }`}
            onClick={() => toggle(day.iso)}
          >
            {t(day.label)}
          </button>
        ))}
      </div>

      {week.length === 0 && (
        /* Refused at the API and in the contract too. With no working days a
           clock can never elapse: nothing is ever late, nothing escalates, and
           the system reports perfect compliance for ever. */
        <p role="alert" className="mt-3 mb-0 text-sm" style={{ color: 'var(--tone-rejected)' }}>
          {t("A working week needs at least one working day, or no SLA clock can ever elapse.")}
        </p>
      )}

      <button
        type="button"
        className="mt-3 rounded border border-border px-3 py-1.5 text-sm"
        disabled={busy || week.length === 0}
        onClick={() => onSaveWeek(calendar.id, week)}
      >
        {t("Save the working week")}
      </button>

      <p className="mt-4 mb-0 text-sm text-content-secondary">
        {t("Timezone")}{' '}{calendar.timezone}{t(". Holidays are excluded from every clock:")}
      </p>
      <ul className="mt-2 list-none space-y-1 p-0 text-sm">
        {data.holidays.length === 0 && (
          <li className="text-content-secondary">{t("No holidays configured.")}</li>
        )}
        {data.holidays.map((entry) => (
          <li key={entry.id}>
            {entry.holidayDate} — {entry.name}
          </li>
        ))}
      </ul>

      <form
        className="mt-3 flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          onAddHoliday(calendar.id, { holidayDate: date, name });
          setDate('');
          setName('');
        }}
      >
        <label className="flex flex-col text-sm">
          <span className="mb-1 text-content-secondary">{t("Date")}</span>
          <input
            type="date"
            className="rounded border border-border px-2 py-1"
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
        </label>
        <label className="flex flex-1 flex-col text-sm">
          <span className="mb-1 text-content-secondary">{t("Holiday")}</span>
          <input
            className="rounded border border-border px-2 py-1"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <button
          type="submit"
          className="rounded border border-border px-3 py-1.5 text-sm"
          disabled={busy || !date || !name.trim()}
        >
          {t("Add")}
        </button>
      </form>
    </Section>
  );
}

function WeightsSection({
  data,
  busy,
  onCreate,
}: {
  data: MasterDataSnapshot;
  busy: boolean;
  onCreate: (body: Record<string, unknown>) => void;
}) {
  const current = data.weightSets.find((set) => set.isDefault) ?? data.weightSets.at(-1);
  // A saved set may use either spelling of its keys (the shipped default uses the
  // category names), so read it the way the calculation does.
  const shown = current ? pqiWeightsFrom(current.weights) : null;
  const [values, setValues] = useState<Record<string, string>>({
    externalNcr: String(shown?.external_ncr ?? 0.25),
    internalNcr: String(shown?.internal_ncr ?? 0.25),
    rfi: String(shown?.rfi ?? 0.25),
    materialInspection: String(shown?.rmi ?? 0.25),
  });
  const [confirmed, setConfirmed] = useState(false);

  const total = Object.values(values).reduce((sum, value) => sum + (Number(value) || 0), 0);
  const balanced = Math.abs(total - 1) < 1e-6;

  return (
    <Section
      heading={t("Project Quality Index weighting")}
      note={t("How App 1's composite index is calculated. Saving creates a NEW VERSION rather than editing this one, so a report approved last month still means what it meant.")}
    >
      {current?.isProvisional && (
        <p className="m-0 mb-3 rounded border border-border bg-tone-info-surface p-3 text-sm">
          {t("The current weighting is marked")}{' '}<strong>{t("provisional")}</strong>{' '}{t("— these numbers are ours, not Algihaz’s. Every PQI on screen carries that badge until Algihaz confirms them.")}
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-4">
        {Object.entries(values).map(([key, value]) => (
          <label key={key} className="flex flex-col text-sm">
            <span className="mb-1 text-content-secondary">{WEIGHT_LABELS[key] ?? key}</span>
            <input
              className="rounded border border-border px-2 py-1"
              inputMode="decimal"
              value={value}
              onChange={(event) => setValues({ ...values, [key]: event.target.value })}
            />
          </label>
        ))}
      </div>

      <p className={`mt-2 mb-0 text-sm ${balanced ? 'text-content-secondary' : ''}`}>
        {t("Total")}{' '}{total.toFixed(3)}.{' '}
        {balanced ? (
          t("Balanced.")
        ) : (
          /* A set summing to 0.9 produces an index that still looks like a
             percentage and sits in the right range, and is wrong by a tenth.
             That is the most expensive kind of wrong, because nobody notices. */
          <span style={{ color: 'var(--tone-rejected)' }}>
            {t("The four weights must add up to exactly 1, or every index is quietly wrong.")}
          </span>
        )}
      </p>

      <label className="mt-3 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={confirmed}
          onChange={(event) => setConfirmed(event.target.checked)}
        />
        {t("Algihaz have confirmed these numbers — remove the provisional badge")}
      </label>

      <button
        type="button"
        className="mt-3 rounded bg-ag-purple px-4 py-2 text-sm font-medium text-white"
        disabled={busy || !balanced}
        onClick={() =>
          onCreate({
            name: confirmed ? 'Confirmed by Algihaz' : 'Revised weighting',
            weights: Object.fromEntries(
              Object.entries(values).map(([key, value]) => [key, Number(value)]),
            ),
            ageingPenaltyPointsPerPct: Number(current?.ageingPenaltyPointsPerPct ?? 0.5),
            ageingPenaltyCap: Number(current?.ageingPenaltyCap ?? 25),
            isProvisional: !confirmed,
            makeDefault: true,
          })
        }
      >
        {t("Save as a new version")}
      </button>

      <p className="mt-3 mb-0 text-xs text-content-secondary">
        {data.weightSets.length}{' '}{t("version")}{data.weightSets.length === 1 ? '' : 's'}{' '}{t("on record. Approved reports keep pointing at the one that produced them.")}
      </p>
    </Section>
  );
}

/** The weights' names on screen — the form's keys are not words (pass 9). */
const WEIGHT_LABELS: Record<string, string> = {
  externalNcr: QAQC_METRIC_CATEGORY_LABELS.external_ncr,
  internalNcr: QAQC_METRIC_CATEGORY_LABELS.internal_ncr,
  rfi: QAQC_METRIC_CATEGORY_LABELS.rfi,
  materialInspection: `${QAQC_METRIC_CATEGORY_LABELS.rmi} (material inspection)`,
};

/** "2, 1" → ["2", "1"]: a comma list typed into one box. */
function parseList(value: string): string[] {
  return value
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
}

function EscalationSection({
  data,
  busy,
  onToggle,
  onPatch,
}: {
  data: MasterDataSnapshot;
  busy: boolean;
  onToggle: (id: string, isEnabled: boolean) => void;
  onPatch: (id: string, body: Record<string, unknown>) => void;
}) {
  return (
    <Section
      heading={t("Escalation matrix")}
      note={t("Who gets chased, after how long, and at what priority. Every number here can be changed on this screen.")}
    >
      <ul className="m-0 list-none space-y-3 p-0">
        {data.escalationRules.map((rule) => (
          <li key={rule.id} className="rounded border border-border p-3 text-sm">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <strong>{rule.name}</strong>
              <span className="text-xs text-content-secondary">
                {rule.app} · {rule.triggerEvent}
                {rule.priority ? ` · ${rule.priority}` : ''}
              </span>
            </div>

            <div className="mt-2 flex flex-wrap items-end gap-3">
              <label className="flex flex-col text-sm">
                <span className="mb-1 text-content-secondary">{t("SLA (working days)")}</span>
                <input
                  type="number"
                  min={0}
                  className="w-24 rounded border border-border px-2 py-1"
                  defaultValue={rule.slaWorkingDays}
                  onBlur={(event) => {
                    const next = Number(event.target.value);
                    if (next !== rule.slaWorkingDays) onPatch(rule.id, { slaWorkingDays: next });
                  }}
                />
              </label>

              <label className="flex flex-col text-sm">
                <span className="mb-1 text-content-secondary">{t("Remind (working days before)")}</span>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="e.g. 2, 1"
                  className="w-32 rounded border border-border px-2 py-1"
                  defaultValue={rule.reminderOffsetDays.join(', ')}
                  onBlur={(event) => {
                    const next = parseList(event.target.value).map(Number);
                    if (next.some((value) => !Number.isInteger(value) || value < 0)) return;
                    if (next.join(',') !== rule.reminderOffsetDays.join(',')) {
                      onPatch(rule.id, { reminderOffsetDays: next });
                    }
                  }}
                />
              </label>

              <label className="flex flex-col text-sm">
                <span className="mb-1 text-content-secondary">{t("Reminded roles")}</span>
                <input
                  type="text"
                  placeholder={t("e.g. approver_reviewer")}
                  className="w-56 rounded border border-border px-2 py-1"
                  defaultValue={rule.reminderRecipientRoleKeys.join(', ')}
                  onBlur={(event) => {
                    const next = parseList(event.target.value);
                    if (next.join(',') !== rule.reminderRecipientRoleKeys.join(',')) {
                      onPatch(rule.id, { reminderRecipientRoleKeys: next });
                    }
                  }}
                />
              </label>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={rule.isEnabled}
                  disabled={busy}
                  onChange={(event) => onToggle(rule.id, event.target.checked)}
                />
                {t("Enabled")}
              </label>
            </div>

            {rule.levels.length > 0 && (
              <p className="mt-2 mb-0 text-xs text-content-secondary">
                {rule.levels
                  .map(
                    (level) =>
                      t('{0} after {1} days, to {2}', {
                        0: level.level,
                        1: level.afterDays,
                        2: level.recipientRoleKeys.join(', '),
                      }),
                  )
                  .join(' · ')}
              </p>
            )}

            {rule.reminderOffsetDays.length > 0 && rule.reminderRecipientRoleKeys.length === 0 && (
              <p className="mt-2 mb-0 text-xs text-content-secondary">
                {t("Reminders reach only the person the clock runs against. For an approval, name the approver role.")}
              </p>
            )}

            {!rule.isEnabled && (
              <p className="mt-2 mb-0 text-xs" style={{ color: 'var(--tone-rejected)' }}>
                {t("Disabled — nothing matching this rule starts a clock, so nobody is chased.")}
              </p>
            )}
          </li>
        ))}
      </ul>
    </Section>
  );
}

function BenchmarkSection({
  data,
  busy,
  onCreate,
}: {
  data: MasterDataSnapshot;
  busy: boolean;
  onCreate: (body: Record<string, unknown>) => void;
}) {
  const [metricKey, setMetricKey] = useState('');
  const [targetValue, setTargetValue] = useState('');
  const [comparison, setComparison] = useState<'gte' | 'lte'>('gte');
  const [app, setApp] = useState('qaqc');

  return (
    <Section
      heading={t("Target benchmarks")}
      note={t("The thresholds escalation compares against — “closure rate below target for 2 consecutive months” uses the target set here.")}
    >
      <ul className="m-0 mb-4 list-none space-y-1 p-0 text-sm">
        {data.benchmarks.length === 0 && (
          <li className="text-content-secondary">{t("No targets configured.")}</li>
        )}
        {data.benchmarks.map((row) => (
          <li key={row.id}>
            <span className="text-content-secondary">{row.app}</span> · {row.metricKey}{' '}
            {row.comparison === 'gte' ? '≥' : '≤'} {row.targetValue}
          </li>
        ))}
      </ul>

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          onCreate({ app, metricKey, targetValue: Number(targetValue), comparison });
          setMetricKey('');
          setTargetValue('');
        }}
      >
        <label className="flex flex-col text-sm">
          <span className="mb-1 text-content-secondary">{t("Application")}</span>
          <select
            className="rounded border border-border px-2 py-1"
            value={app}
            onChange={(event) => setApp(event.target.value)}
          >
            <option value="qaqc">{t("QA/QC")}</option>
            <option value="lesson-learned">{t("Lesson Learned")}</option>
            <option value="audit">{t("QMS Audit")}</option>
          </select>
        </label>
        <label className="flex flex-col text-sm">
          <span className="mb-1 text-content-secondary">{t("Metric")}</span>
          <input
            className="rounded border border-border px-2 py-1"
            value={metricKey}
            onChange={(event) => setMetricKey(event.target.value)}
            placeholder={t("closure_rate")}
          />
        </label>
        <label className="flex flex-col text-sm">
          <span className="mb-1 text-content-secondary">{t("Direction")}</span>
          <select
            className="rounded border border-border px-2 py-1"
            value={comparison}
            onChange={(event) => setComparison(event.target.value as 'gte' | 'lte')}
          >
            <option value="gte">{t("Higher is better (≥)")}</option>
            <option value="lte">{t("Lower is better (≤)")}</option>
          </select>
        </label>
        <label className="flex flex-col text-sm">
          <span className="mb-1 text-content-secondary">{t("Target")}</span>
          <input
            className="w-24 rounded border border-border px-2 py-1"
            inputMode="decimal"
            value={targetValue}
            onChange={(event) => setTargetValue(event.target.value)}
          />
        </label>
        <button
          type="submit"
          className="rounded border border-border px-3 py-1.5 text-sm"
          disabled={busy || !metricKey.trim() || !targetValue}
        >
          {t("Add target")}
        </button>
      </form>

      <p className="mt-2 mb-0 text-xs text-content-secondary">
        {t("The direction is asked for rather than assumed: a target with it the wrong way round reports success exactly when things are at their worst.")}
      </p>
    </Section>
  );
}

function CodeListSection({
  heading,
  note,
  rows,
  busy,
  onCreate,
  onRetire,
}: {
  heading: string;
  note: string;
  rows: { id: string; label: string }[];
  busy: boolean;
  onCreate: (code: string, name: string) => void;
  onRetire: (id: string) => void;
}) {
  const [code, setCode] = useState('');
  const [name, setName] = useState('');

  return (
    <Section heading={heading} note={note}>
      <ul className="m-0 mb-4 list-none space-y-1 p-0 text-sm">
        {rows.map((row) => (
          <li key={row.id} className="flex items-center justify-between gap-2">
            <span>{t(row.label)}</span>
            <ConfirmButton
              label={t("Retire")}
              question={t("Retire {0}? Past records keep it; it stops being offered.", { 0: row.label })}
              confirmLabel={t("Yes, retire it")}
              className="rounded border border-border px-2 py-0.5 text-xs"
              disabled={busy}
              onConfirm={() => onRetire(row.id)}
            />
          </li>
        ))}
      </ul>

      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          onCreate(code.toUpperCase(), name);
          setCode('');
          setName('');
        }}
      >
        <label className="flex flex-col text-sm">
          <span className="mb-1 text-content-secondary">{t("Code")}</span>
          <input
            className="w-32 rounded border border-border px-2 py-1"
            value={code}
            onChange={(event) => setCode(event.target.value)}
          />
        </label>
        <label className="flex flex-1 flex-col text-sm">
          <span className="mb-1 text-content-secondary">{t("Name")}</span>
          <input
            className="rounded border border-border px-2 py-1"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <button
          type="submit"
          className="rounded border border-border px-3 py-1.5 text-sm"
          disabled={busy || !code.trim() || !name.trim()}
        >
          {t("Add")}
        </button>
      </form>

      <p className="mt-2 mb-0 text-xs text-content-secondary">
        {t("Retiring is never a deletion — the row keeps its history, existing records still resolve, and the code becomes available again.")}
      </p>
    </Section>
  );
}

function CategorisationSection({ data }: { data: MasterDataSnapshot }) {
  const byCategory = new Map<string, typeof data.categorisations>();
  for (const row of data.categorisations) {
    byCategory.set(row.category, [...(byCategory.get(row.category) ?? []), row]);
  }

  return (
    <Section
      heading={t("Categorisation & risk")}
      note={t("Feeds App 2's dropdowns and the risk half of App 3's priority matrix — a risk level here is what a finding's classification is crossed with to reach P1–P6.")}
    >
      {byCategory.size === 0 && <p className="m-0 text-sm text-content-secondary">{t("Nothing configured.")}</p>}
      {[...byCategory.entries()].map(([category, rows]) => (
        <div key={category} className="mb-3">
          <h3 className="m-0 text-sm font-semibold">{category}</h3>
          <ul className="mt-1 list-none space-y-1 p-0 text-sm">
            {rows.map((row) => (
              <li key={row.id}>
                {t(row.label)}
                {row.riskLevel && (
                  <span className="ms-2 text-xs text-content-secondary">{t("risk")}{' '}{row.riskLevel}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </Section>
  );
}

function ProjectsSection({ data }: { data: MasterDataSnapshot }) {
  return (
    <Section
      heading={t("Projects")}
      note={t("Synced from the {0}. Read-only here: projects are created on the platform, and arrive here by the sync.", { 0: data.projects.source })}
    >
      <ul className="m-0 list-none space-y-1 p-0 text-sm">
        {data.projects.rows.map((project) => (
          <li key={project.id}>
            {project.code} — {project.name}
          </li>
        ))}
      </ul>
    </Section>
  );
}
